import React, { useState, useEffect, useCallback, useRef } from "react";
import InvalidToken from "./components/InvalidToken";
import ConnectionError from "./components/ConnectionError";
import MyKad from "../mykad_verification/MyKad";
import MyPR from "../mypr_verification/MyPR";
import MyTentera from "../mytentera_verification/MyTentera";
import Faceliveness from "../faceliveness/FaceLiveness";
import Passport from "../passport_verification/Passport";
import Success from "./components/Success";

import { useEKYCManagerService } from "./services/useEKYCManagerService";
import { initShield, getDeviceIntelligence } from "../../utils/fingerprint";
import isMobileDevice from "../../utils/isMobileDevice";
import EkycLoader from "../../components/loader/EkycLoader";
import ContactSupport from "./components/ContactSupport";
import {
  initDiagnostics,
  shutdownDiagnostics,
  trackScreen,
  trackBootFailure,
  useDiagnosticsState,
  shouldShowContactSupport,
  diagnosticsStore,
} from "../../diagnostics";

const BOOT_FAILURE_KEY = "ekycBootFailure";
const DESKTOP_POLL_MS = 30000;

const EKYCManager = () => {
  const { getNextStep, getValidationRecordFromToken, getSessionInfo, saveDeviceIntelligence } = useEKYCManagerService();

  const diagnostics = useDiagnosticsState();

  const [currentStep, setCurrentStep] = useState(null);
  const [isValidToken, setIsValidToken] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [validation, setValidation] = useState(null);
  const [redirectURL, setRedirectURL] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpireAt, setSessionExpireAt] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const [hasTriggered, setHasTriggered] = useState(false);
  const isDesktop = useRef(!isMobileDevice());

  // Read token from URL or sessionStorage
  const [initialToken] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("token") || sessionStorage.getItem("ekycSessionToken");
  });

  useEffect(() => {
    if (!initialToken) {
      setIsValidToken(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setConnectionError(false);

    getValidationRecordFromToken(initialToken)
      .then((result) => {
        if (cancelled) return;
        if (result.status !== "success") {
          setIsValidToken(false);
        } else {
          // Strip token from URL if present
          if (window.location.search.includes("token=")) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          setValidation(result.data);
          setCurrentStep(result.data?.CurrentStep);

          const redirect = result.data?.Metadata?.RedirectURL;
          setRedirectURL(redirect);

          // Store in sessionStorage for DesktopQrModal status check
          if (result.data?.ValidationID) {
            sessionStorage.setItem("ekycValidationID", result.data.ValidationID);
          }
          if (redirect) {
            sessionStorage.setItem("ekycRedirectURL", redirect);
          }

          setSessionExpireAt(result.data?.SessionExpireAt || null);
        }
        // A fresh session starts on the Trigger step, which the render switch
        // has no case for. Keep the loader up; the Trigger effect below clears
        // it once get-next-step resolves. Otherwise InvalidToken paints for a
        // frame before the first real step appears.
        if (result.data?.CurrentStep?.ComponentType !== "Trigger") {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setConnectionError(true);
        setIsLoading(false);
        try {
          sessionStorage.setItem(BOOT_FAILURE_KEY, JSON.stringify({
            ts: Date.now(),
            error: err.message || "unknown",
            attempt: retryCount + 1,
          }));
        } catch (e) { /* noop */ }
      });

    return () => {
      cancelled = true;
    };
  }, [initialToken, getValidationRecordFromToken, retryCount]);

  const handleRetry = useCallback(() => {
    setConnectionError(false);
    setIsLoading(true);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    if (!validation?.ValidationID) return;

    let isMounted = true;

    (async () => {
      try {
        const siteId = process.env.REACT_APP_SHIELD_SITE_ID;
        if (!siteId) return;

        await initShield(siteId);
        const clientId = validation.ClientID;
        const res = await getDeviceIntelligence({
          clientId,
          userAttrData: {
            validation_id: validation.ValidationID,
          },
        });

        if (!isMounted) return;

        const sessionId = res?.result?.session_id || null;
        const deviceIntelligence = res?.result?.device_intelligence || null;

        await saveDeviceIntelligence(validation.ValidationID, {
          sessionId,
          deviceIntelligence,
        });
      } catch (e) {
        // optional: swallow or log
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [validation, initShield, getDeviceIntelligence, saveDeviceIntelligence]);

  // eKYC client diagnostics — capture the whole session. Anchored here so a
  // single init covers every document module rendered below. Collection runs
  // regardless of whether the user contacts support.
  useEffect(() => {
    if (!validation?.ValidationID) return;
    initDiagnostics(validation.ValidationID, {
      clientId: validation.ClientID,
      appVersion: process.env.REACT_APP_VERSION || null,
    });

    // Replay any boot failure that happened before diagnostics could init.
    try {
      const raw = sessionStorage.getItem(BOOT_FAILURE_KEY);
      if (raw) {
        sessionStorage.removeItem(BOOT_FAILURE_KEY);
        trackBootFailure(JSON.parse(raw));
      }
    } catch (e) { /* noop */ }

    return () => {
      shutdownDiagnostics();
    };
  }, [validation?.ValidationID]);

  // Record the screen/step timeline ("where did the user stop").
  useEffect(() => {
    if (currentStep?.ComponentType) {
      trackScreen(currentStep.ComponentType);
    }
  }, [currentStep?.ComponentType]);

  useEffect(() => {
    if (!validation?.ValidationID) return;
    setHasTriggered(false);
  }, [validation?.ValidationID]);

  // Desktop session sync: poll the server for the latest SessionExpireAt so the
  // local timer stays in sync with mobile-side extensions. Paused when the tab
  // is hidden to avoid keeping the session alive from an abandoned tab.
  useEffect(() => {
    if (!isDesktop.current || !validation?.ValidationID) return;

    let timer = null;
    let stopped = false;

    const poll = async () => {
      if (stopped || document.visibilityState === "hidden") return;
      try {
        const res = await getSessionInfo(validation.ValidationID);
        if (stopped) return;
        if (res.status === "success" && res.data?.SessionExpireAt) {
          setSessionExpireAt(res.data.SessionExpireAt);
        }
      } catch (e) {
        // swallow: polling is best-effort
      }
    };

    const schedule = () => {
      if (stopped) return;
      timer = setTimeout(() => {
        poll().then(schedule);
      }, DESKTOP_POLL_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && !stopped) {
        poll().then(schedule);
      } else {
        clearTimeout(timer);
        timer = null;
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    schedule();

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [validation?.ValidationID, getSessionInfo]);

  // Session expiry: instead of expiring locally on the dot, do a final
  // server check so a mobile-side extension made between polls is not missed.
  useEffect(() => {
    if (!sessionExpireAt) return;

    const timeRemaining = sessionExpireAt - Date.now();
    if (timeRemaining <= 0) {
      setIsValidToken(false);
      return;
    }

    const timer = setTimeout(async () => {
      if (isDesktop.current && validation?.ValidationID) {
        try {
          const res = await getSessionInfo(validation.ValidationID);
          if (res.status === "success" && res.data?.SessionExpireAt) {
            const serverExpireAt = res.data.SessionExpireAt;
            if (serverExpireAt > Date.now()) {
              setSessionExpireAt(serverExpireAt);
              return;
            }
          }
        } catch (e) {
          // server unreachable at expiry: expire locally
        }
      }
      setIsValidToken(false);
    }, timeRemaining);

    return () => {
      clearTimeout(timer);
    };
  }, [sessionExpireAt, validation?.ValidationID, getSessionInfo]);

  const onSessionUpdate = (session) => {
    if (session?.SessionExpireAt) {
      setSessionExpireAt(session.SessionExpireAt);
    }
  };

  useEffect(() => {
    if (
      currentStep?.ComponentType === "Trigger" &&
      validation &&
      !hasTriggered
    ) {
      setIsLoading(true);
      setHasTriggered(true);
      getNextStep(validation.ValidationID)
        .then((result) => {
          if (result.status === "success") {
            setValidation(result.data.Validation);
            setCurrentStep(result.data.CurrentStep);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [currentStep, validation, getNextStep, hasTriggered]);

  if (isLoading) {
    return <EkycLoader />;
  }

  if (connectionError) {
    return <ConnectionError onRetry={handleRetry} />;
  }

  if (!isValidToken) {
    return <InvalidToken />;
  }

  // After 3 document-scan failures or 2 server timeouts, offer Contact support.
  if (shouldShowContactSupport(diagnostics)) {
    return (
      <ContactSupport
        referenceCode={validation?.ValidationID}
        counts={diagnostics}
        onContinue={() => diagnosticsStore.reset()}
      />
    );
  }

  return (
    <div>
      {(function () {
        switch (currentStep?.ComponentType ?? "Success") {
          case "MyKad":
            return (
              <MyKad
                validation={validation}
                onSessionUpdate={onSessionUpdate}
                onComplete={(data) => {
                  setIsLoading(true);
                  getNextStep(data.validationID).then((result) => {
                    if (result.status === "success") {
                      setValidation(result.data.Validation);
                      setCurrentStep(result.data.CurrentStep);
                      onSessionUpdate(result.session);
                      setIsLoading(false);
                    }
                  });
                }}
              />
            );

          case "MyPR":
            return (
              <MyPR
                validation={validation}
                onSessionUpdate={onSessionUpdate}
                onComplete={(data) => {
                  setIsLoading(true);
                  getNextStep(data.validationID).then((result) => {
                    if (result.status === "success") {
                      setValidation(result.data.Validation);
                      setCurrentStep(result.data.CurrentStep);
                      onSessionUpdate(result.session);
                      setIsLoading(false);
                    }
                  });
                }}
              />
            );

          case "MyTentera":
            return (
              <MyTentera
                validation={validation}
                onSessionUpdate={onSessionUpdate}
                onComplete={(data) => {
                  setIsLoading(true);
                  getNextStep(data.validationID).then((result) => {
                    if (result.status === "success") {
                      setValidation(result.data.Validation);
                      setCurrentStep(result.data.CurrentStep);
                      onSessionUpdate(result.session);
                      setIsLoading(false);
                    }
                  });
                }}
              />
            );

          case "FaceLiveness":
            return (
              <Faceliveness
                validation={validation}
                onSessionUpdate={onSessionUpdate}
                onComplete={(data) => {
                  setIsLoading(true);
                  getNextStep(data.validationID).then((result) => {
                    if (result.status === "success") {
                      setValidation(result.data.Validation);
                      setCurrentStep(result.data.CurrentStep);
                      onSessionUpdate(result.session);
                      setIsLoading(false);
                    }
                  });
                }}
              />
            );

          case "Passport":
            return (
              <Passport
                validation={validation}
                onSessionUpdate={onSessionUpdate}
                onComplete={(data) => {
                  setIsLoading(true);
                  getNextStep(data.validationID).then((result) => {
                    if (result.status === "success") {
                      setValidation(result.data.Validation);
                      setCurrentStep(result.data.CurrentStep);
                      onSessionUpdate(result.session);
                      setIsLoading(false);
                    }
                  });
                }}
              />
            );

          case "Success":
            return (
              <Success
                onDone={() => {
                  if (redirectURL) {
                    window.location.replace(redirectURL);
                  }
                }}
              />
            );

          default:
            return <InvalidToken />;
        }
      })()}
    </div>
  );
};

export default EKYCManager;
